import {
  ActPersonalizationSourceResponseSchema,
  type ActPersonalizationSourceResponse,
  type CabinetLetterheadListResponse,
  type CabinetLetterheadPatch,
  type CabinetLetterheadResponse,
  type LetterheadResponse,
} from '@hector/shared-types';
import { apiClient, getAuthToken } from '@/lib/apiClient';
import {
  EDITABLE_MARKER_KEYS,
  type EditableHeadingLevel,
  type EditableMarkerKey,
  type FormattingSettings,
} from '@/types/formatting';

export type FormattingStyleKey = EditableHeadingLevel | 'normal' | EditableMarkerKey;
export interface FormattingPropertyDiff {
  readonly styleKey: FormattingStyleKey | 'fontName';
  readonly property: string;
  readonly label: string;
  readonly persistedValue: string;
  readonly draftValue: string;
}
export interface ActPersonalizationSource {
  readonly redactionStyle: ActPersonalizationSourceResponse['redactionStyle'];
  readonly structureSource: ActPersonalizationSourceResponse['structureSource'];
  readonly canEditCabinetFormatting: boolean;
}

export async function fetchActPersonalizationSource(): Promise<ActPersonalizationSource> {
  const source = ActPersonalizationSourceResponseSchema.parse(
    await apiClient.get<unknown>('/act-personalization/source'),
  );

  return {
    redactionStyle: source.redactionStyle,
    structureSource: source.structureSource,
    canEditCabinetFormatting: true,
  };
}

// `color`/`leftIndent`/`rightIndent` are optional on HeadingStyle + ParagraphSettings and ARE
// applied by the add-in named-style engine (`setupHectorStyles.ts`) — exposed in the web editor
// since 2026-07-26, so the diff must cover them too. Markers carry no `color` in the contract.
const HEADING_PROPERTIES = ['fontSize', 'bold', 'italic', 'underline', 'color', 'alignment', 'leftIndent', 'rightIndent', 'spaceBefore', 'spaceAfter'] as const;
const PARAGRAPH_PROPERTIES = ['fontSize', 'alignment', 'color', 'leftIndent', 'rightIndent', 'spaceAfter', 'lineSpacing', 'firstLineIndent'] as const;
const MARKER_PROPERTIES = ['fontSize', 'bold', 'italic', 'underline', 'alignment', 'lineBreakBefore', 'lineBreakAfter', 'spaceBefore', 'spaceAfter'] as const;

const FORMATTING_PROPERTY_LABELS: Record<string, string> = {
  fontName: 'Police globale',
  fontSize: 'Taille',
  bold: 'Gras',
  italic: 'Italique',
  underline: 'Souligné',
  alignment: 'Alignement',
  lineBreakBefore: 'Ligne vide avant',
  lineBreakAfter: 'Ligne vide après',
  color: 'Couleur',
  leftIndent: 'Retrait gauche',
  rightIndent: 'Retrait droite',
  spaceBefore: 'Espace avant',
  spaceAfter: 'Espace après',
  lineSpacing: 'Interligne',
  firstLineIndent: 'Retrait 1ère ligne',
};

const MARKER_EFFECTIVE_DEFAULTS: Partial<Record<string, number>> = {
  fontSize: 11,
  spaceBefore: 0,
  spaceAfter: 6,
};

function getEffectiveFormattingPropertyValue(
  styleKey: FormattingStyleKey,
  property: string,
  value: unknown,
): unknown {
  if (
    (EDITABLE_MARKER_KEYS as readonly string[]).includes(styleKey)
    && value === undefined
    && property in MARKER_EFFECTIVE_DEFAULTS
  ) {
    // Keep diff semantics aligned with the marker editor, which renders these
    // omitted persisted values as DOCX-compatible defaults rather than blanks.
    return MARKER_EFFECTIVE_DEFAULTS[property];
  }
  // Indents are optional on headings/paragraph; an absent indent means 0 pt (Word default),
  // so an edit from "unset" to 0 (and back) must NOT register as an unsaved change.
  if ((property === 'leftIndent' || property === 'rightIndent') && value === undefined) {
    return 0;
  }
  return value;
}

function formatFormattingPropertyValue(property: string, value: unknown): string {
  // `undefined`/`null` color = "Automatique" (inherits the global/body colour) — the editor's
  // Auto state. A concrete value is a normalised hex like "#1A1A1A".
  if (property === 'color') return value ? String(value).toUpperCase() : 'Automatique';
  if (property === 'fontSize' || property === 'spaceBefore' || property === 'spaceAfter') return `${value} pt`;
  if (property === 'firstLineIndent' || property === 'leftIndent' || property === 'rightIndent') return `${value} pt`;
  // Décimale française : 1,15 / 1,5. Le libellé doit correspondre au <select> Interligne
  // de FormattingEditor, sinon le récap d'écarts affiche « 1.15 » face à une option « 1,15 ».
  if (property === 'lineSpacing') return String(value).replace('.', ',');
  if (property === 'bold' || property === 'italic' || property === 'lineBreakBefore' || property === 'lineBreakAfter') return value === true ? 'Oui' : 'Non';
  if (property === 'underline') return value === 'none' ? 'Non' : 'Oui';
  if (property === 'alignment') {
    return { left: 'Gauche', center: 'Centré', right: 'Droite', justified: 'Justifié' }[String(value)] ?? String(value);
  }
  return String(value);
}

function collectFormattingPropertyDiffs(
  diffs: FormattingPropertyDiff[],
  styleKey: FormattingStyleKey,
  properties: readonly string[],
  persisted: object,
  draft: object,
): void {
  properties.forEach(property => {
    const persistedValue = getEffectiveFormattingPropertyValue(
      styleKey,
      property,
      (persisted as Record<string, unknown>)[property],
    );
    const draftValue = getEffectiveFormattingPropertyValue(
      styleKey,
      property,
      (draft as Record<string, unknown>)[property],
    );
    if (persistedValue === draftValue) return;
    diffs.push({
      styleKey,
      property,
      label: FORMATTING_PROPERTY_LABELS[property] ?? property,
      persistedValue: formatFormattingPropertyValue(property, persistedValue),
      draftValue: formatFormattingPropertyValue(property, draftValue),
    });
  });
}

/** Returns every editable property whose draft differs from persisted cabinet formatting. */
export function diffFormattingProperties(
  persisted: FormattingSettings,
  draft: FormattingSettings,
): FormattingPropertyDiff[] {
  const diffs: FormattingPropertyDiff[] = [];
  if (persisted.fontName !== draft.fontName) {
    diffs.push({
      styleKey: 'fontName',
      property: 'fontName',
      label: FORMATTING_PROPERTY_LABELS.fontName ?? 'Police globale',
      persistedValue: persisted.fontName,
      draftValue: draft.fontName,
    });
  }
  (['h1', 'h2', 'h3', 'h4'] as const).forEach(key => {
    collectFormattingPropertyDiffs(diffs, key, HEADING_PROPERTIES, persisted.headings[key], draft.headings[key]);
  });
  collectFormattingPropertyDiffs(diffs, 'normal', PARAGRAPH_PROPERTIES, persisted.paragraph, draft.paragraph);
  EDITABLE_MARKER_KEYS.forEach(key => {
    collectFormattingPropertyDiffs(diffs, key, MARKER_PROPERTIES, persisted.markers[key], draft.markers[key]);
  });
  return diffs;
}

/** Returns visible editor entries whose draft differs from persisted cabinet formatting. */
export function diffFormattingStyleKeys(
  persisted: FormattingSettings,
  draft: FormattingSettings,
): FormattingStyleKey[] {
  const changed = new Set(diffFormattingProperties(persisted, draft).map(diff => diff.styleKey));
  return (['h1', 'h2', 'h3', 'h4', 'normal', ...EDITABLE_MARKER_KEYS] as const)
    .filter((styleKey): styleKey is FormattingStyleKey => changed.has(styleKey));
}

export async function saveRedactionStyle(styleMarkdown: string): Promise<void> {
  await apiClient.patch('/act-personalization/redaction-style', { styleMarkdown });
}

// ── En-tête cabinet (letterhead) — Lot B, 2026-07-26 ──────────────────────────
// Chemin de l'en-tête PAR DÉFAUT : lecture des réglages, octets de l'image, et remplacement du
// fichier. Le PATCH des réglages au singulier (`PATCH …/letterhead`) n'a plus de client web
// depuis le 2026-09-04 — les réglages s'écrivent PAR EN-TÊTE via `updateCabinetLetterhead`
// ci-dessous. La route back-end, elle, reste : d'autres clients la consomment.

/** GET la config d'en-tête + une URL signée (1 h) de l'image (null si aucune). */
export async function fetchCabinetLetterhead(cabinetId: string): Promise<LetterheadResponse> {
  return apiClient.get<LetterheadResponse>(`/cabinet-settings/${cabinetId}/letterhead`);
}

/**
 * Récupère les OCTETS de l'image d'en-tête, en même-origine (proxy backend).
 *
 * NE PAS remplacer par un `fetch(imageUrl)` sur l'URL S3 signée que rend
 * {@link fetchCabinetLetterhead} : le seau OVH n'a aucune configuration CORS — mesuré le
 * 2026-08-17, préflight `403` et zéro `access-control-allow-origin` pour TOUTES les origines
 * déclarées, production comprise. Une balise `<img>` s'en moque, un `.blob()` non. C'est
 * précisément ce qui vidait l'export Word de son en-tête, sans erreur visible.
 *
 * L'URL signée reste parfaite pour les aperçus `<img>` ; seuls les consommateurs d'octets
 * passent par ici. Même choix que `storageService`, qui proxifie déjà les pièces.
 */
export async function fetchCabinetLetterheadImageBlob(cabinetId: string): Promise<Blob> {
  // Même expression que `storageService.ts` : `apiClient` n'exporte pas sa base, et l'ajouter
  // pour un seul appelant élargirait sa surface publique sans bénéfice.
  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000/api';
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE_URL}/cabinet-settings/${cabinetId}/letterhead/image`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error(
      `Téléchargement de l'image d'en-tête refusé (HTTP ${response.status}) pour le cabinet ${cabinetId}`,
    );
  }
  return response.blob();
}

/**
 * REMPLACE le fichier de l'en-tête par DÉFAUT (multipart `file` ; PNG/JPEG/PDF ≤ 2 Mo côté
 * serveur, un PDF étant rastérisé à la réception).
 *
 * Cette route ne nomme aucun en-tête : elle écrit sur le défaut du cabinet. Déposer un en-tête
 * NOMMÉ passe par {@link createCabinetLetterhead} ; il n'existe pas, au 2026-09-04, de route
 * qui remplacerait le fichier d'un en-tête nommé autre que le défaut.
 */
export async function uploadCabinetLetterheadImage(cabinetId: string, file: File): Promise<LetterheadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<LetterheadResponse>(`/cabinet-settings/${cabinetId}/letterhead/image`, formData);
}

// ── Collection d'en-têtes NOMMÉS — migration 163, 2026-09-03 ──────────────────
// Les fonctions ci-dessus (au SINGULIER) restent le chemin de l'en-tête PAR DÉFAUT ; celles-ci
// portent l'IDENTITÉ de chaque en-tête (nom, note d'usage, promotion, suppression).

/** Tous les en-têtes du cabinet, défaut en tête, avec une URL d'aperçu signée par entrée. */
export async function fetchCabinetLetterheads(cabinetId: string): Promise<CabinetLetterheadListResponse> {
  return apiClient.get<CabinetLetterheadListResponse>(`/cabinet-settings/${cabinetId}/letterheads`);
}

/**
 * Dépose un NOUVEL en-tête nommé (multipart : fichier + nom + note d'usage).
 *
 * Le fichier est un Word (il devient le PAPIER de l'en-tête) ou une image (son LOGO) : c'est
 * le serveur qui tranche, le champ `file` est le même.
 *
 * Le nom est obligatoire côté serveur : c'est par lui que l'avocat — et l'agent — désigneront
 * cet en-tête. Un doublon de nom revient en 400 nommant le conflit, pas en 500.
 */
export async function createCabinetLetterhead(
  cabinetId: string,
  input: { file: File; name: string; usageNote?: string },
): Promise<CabinetLetterheadResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('name', input.name);
  if (input.usageNote !== undefined) formData.append('usageNote', input.usageNote);
  return apiClient.post<CabinetLetterheadResponse>(`/cabinet-settings/${cabinetId}/letterheads`, formData);
}

/** Renomme, réécrit la note, promeut en défaut (`isDefault: true` seulement) ou règle le rendu. */
export async function updateCabinetLetterhead(
  cabinetId: string,
  letterheadId: string,
  patch: CabinetLetterheadPatch,
): Promise<CabinetLetterheadResponse> {
  return apiClient.patch<CabinetLetterheadResponse>(
    `/cabinet-settings/${cabinetId}/letterheads/${letterheadId}`,
    patch,
  );
}

/**
 * Dépose le PAPIER d'un en-tête nommé — ou remplace celui qu'il porte déjà (multipart : `file`,
 * un `.docx`). Le serveur n'en garde que l'en-tête, le pied de page et les marges.
 */
export async function uploadCabinetLetterheadDocument(
  cabinetId: string,
  letterheadId: string,
  file: File,
): Promise<CabinetLetterheadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post<CabinetLetterheadResponse>(
    `/cabinet-settings/${cabinetId}/letterheads/${letterheadId}/document`,
    formData,
  );
}

/** Supprime l'en-tête et ses fichiers. Le serveur promeut un successeur si c'était le défaut. */
export async function deleteCabinetLetterhead(
  cabinetId: string,
  letterheadId: string,
): Promise<{ deleted: boolean }> {
  return apiClient.delete<{ deleted: boolean }>(
    `/cabinet-settings/${cabinetId}/letterheads/${letterheadId}`,
  );
}

/**
 * Octets de l'image d'un en-tête NOMMÉ, en même-origine.
 *
 * Même raison d'être que {@link fetchCabinetLetterheadImageBlob} — le seau OVH n'a aucune
 * configuration CORS, donc un `.blob()` sur l'URL signée est bloqué par le navigateur. C'est
 * cette fonction qui alimente la pose d'un en-tête CHOISI dans le canvas.
 */
export async function fetchCabinetLetterheadImageBlobById(
  cabinetId: string,
  letterheadId: string,
): Promise<Blob> {
  const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000/api';
  const token = await getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}/cabinet-settings/${cabinetId}/letterheads/${letterheadId}/image`,
    { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
  );
  if (!response.ok) {
    throw new Error(
      `Téléchargement de l'image d'en-tête refusé (HTTP ${response.status}) pour l'en-tête ${letterheadId} du cabinet ${cabinetId}`,
    );
  }
  return response.blob();
}
